import React, { useState, useRef, useEffect, useMemo } from "react";

const CustomSelect = ({
  options = [],
  placeholder = "Select",
  value,
  onChange,
  width,
  height,
  fontSize,
  optionFontSize,
  disable = false,
  error = false,
  helperText,
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const selectRef = useRef(null);
  const searchRef = useRef(null);

  const filteredOptions = useMemo(() => {
    return options.filter((op) =>
      op.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearch("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen, searchable]);

  const handleKeyDown = (e) => {
    if (!isOpen && ["ArrowDown", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      setIsOpen(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1) % filteredOptions.length);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h - 1 < 0 ? filteredOptions.length - 1 : h - 1));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      filteredOptions[highlighted] &&
        handleSelect(filteredOptions[highlighted]);
    }

    if (["Escape", "Tab"].includes(e.key)) {
      setIsOpen(false);
      setSearch("");
    }
  };

  return (
    <div ref={selectRef} className={`relative w-${width} font-poppins`}>
      {/* Select Box */}
      <div
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={() => !disable && setIsOpen((o) => !o)}
        className={`flex justify-between items-center gap-2 ${width} ${height} px-2 py-1
          rounded-md border cursor-pointer focus:outline-none
          ${disable ? "text-[#999]" : ""}
          ${error ? "border-red-500" : "border-[#a48572]/50"}`}
      >
        <p className={`text-${fontSize} text-[#142b70]/70`}>
          {value
            ? options.find((op) => op.value === value)?.label
            : placeholder}
        </p>
        <svg
          width={16}
          height={16}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          <path d="M4 6.5L8 10.5L12 6.5" stroke="#142b70" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-md max-h-56 overflow-hidden">
          {searchable && (
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlighted(0);
              }}
              placeholder="Search..."
              className="w-full px-2 py-1 text-sm border-b focus:outline-none"
            />
          )}

          <div className="max-h-44 overflow-y-auto">
            {filteredOptions.length ? (
              filteredOptions.map((option, idx) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`px-2 py-1 text-${optionFontSize} cursor-pointer
                    ${idx === highlighted ? "bg-[#f5f2f0]" : ""}
                    hover:bg-[#f5f2f0]`}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <p className="px-2 py-2 text-sm text-gray-400">No results</p>
            )}
          </div>
        </div>
      )}

      {error && helperText && (
        <p className="text-xs text-red-600 mt-1">{helperText}</p>
      )}
    </div>
  );
};

export default CustomSelect;
